import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCwIcon, SearchIcon, FilterIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, TrendingUpIcon, TrendingDownIcon, DollarSignIcon, FileTextIcon } from 'lucide-react';
import { Refund, RefundStatus, RefundReason } from '../types/Refund';
export function Refunds() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RefundStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<RefundReason | 'all'>('all');
  // Mock data
  const refunds: Refund[] = [{
    id: '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: 'LKR',
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  }, {
    id: '2',
    refundId: 'REF-2024-002',
    transactionId: 'TXN-2024-1567',
    originalAmount: 87500,
    refundAmount: 43750,
    currency: 'LKR',
    status: 'processing',
    reason: 'product_defective',
    reasonDescription: 'Product arrived damaged, partial refund issued',
    requestedBy: 'Sarah Johnson',
    requestedAt: '2024-01-15T14:20:00Z',
    approvedBy: 'Mike Manager',
    approvedAt: '2024-01-15T15:00:00Z',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '5555',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Mike Manager',
      approverEmail: 'mike.m@merchant.com',
      status: 'approved',
      timestamp: '2024-01-15T15:00:00Z',
      notes: 'Approved partial refund for damaged item'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T14:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Sarah Johnson'
    }, {
      id: '2',
      timestamp: '2024-01-15T14:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-15T15:00:00Z',
      status: 'approved',
      description: 'Refund approved by Mike Manager',
      actor: 'Mike Manager'
    }, {
      id: '4',
      timestamp: '2024-01-15T15:05:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }],
    isPartial: true
  }, {
    id: '3',
    refundId: 'REF-2024-003',
    transactionId: 'TXN-2024-0892',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: 'LKR',
    status: 'completed',
    reason: 'duplicate_charge',
    reasonDescription: 'Duplicate transaction, full refund issued',
    requestedBy: 'Michael Brown',
    requestedAt: '2024-01-10T09:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-10T09:15:00Z',
    processedAt: '2024-01-10T09:20:00Z',
    completedAt: '2024-01-12T10:30:00Z',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    paymentMethod: 'Visa',
    cardLast4: '1234',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-10T09:15:00Z',
      notes: 'Duplicate charge confirmed, approved immediately'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-10T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Michael Brown'
    }, {
      id: '2',
      timestamp: '2024-01-10T09:15:00Z',
      status: 'approved',
      description: 'Refund approved by Admin User',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-10T09:20:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-12T10:30:00Z',
      status: 'completed',
      description: 'Refund successfully completed'
    }],
    isPartial: false
  }, {
    id: '4',
    refundId: 'REF-2024-004',
    transactionId: 'TXN-2024-0654',
    originalAmount: 165000,
    refundAmount: 165000,
    currency: 'LKR',
    status: 'rejected',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund after 30-day window',
    requestedBy: 'Emily Davis',
    requestedAt: '2024-01-08T11:20:00Z',
    rejectedBy: 'Sarah Manager',
    rejectedAt: '2024-01-08T14:00:00Z',
    rejectionReason: 'Refund request exceeds the 30-day return policy window',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    paymentMethod: 'Mastercard',
    cardLast4: '8888',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'rejected',
      timestamp: '2024-01-08T14:00:00Z',
      notes: 'Request outside return policy window'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-08T11:20:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Emily Davis'
    }, {
      id: '2',
      timestamp: '2024-01-08T11:25:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }, {
      id: '3',
      timestamp: '2024-01-08T14:00:00Z',
      status: 'rejected',
      description: 'Refund rejected by Sarah Manager',
      actor: 'Sarah Manager'
    }],
    isPartial: false
  }, {
    id: '5',
    refundId: 'REF-2024-005',
    transactionId: 'TXN-2024-2341',
    originalAmount: 95000,
    refundAmount: 95000,
    currency: 'LKR',
    status: 'requested',
    reason: 'product_not_received',
    reasonDescription: 'Customer claims product was never delivered',
    requestedBy: 'David Wilson',
    requestedAt: '2024-01-19T15:45:00Z',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    paymentMethod: 'Visa',
    cardLast4: '6789',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-19T15:45:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'David Wilson'
    }],
    isPartial: false
  }, {
    id: '6',
    refundId: 'REF-2024-006',
    transactionId: 'TXN-2024-1823',
    originalAmount: 215000,
    refundAmount: 215000,
    currency: 'LKR',
    status: 'failed',
    reason: 'billing_error',
    reasonDescription: 'Billing error detected, refund attempted',
    requestedBy: 'System',
    requestedAt: '2024-01-17T08:00:00Z',
    approvedBy: 'Admin User',
    approvedAt: '2024-01-17T08:05:00Z',
    processedAt: '2024-01-17T08:10:00Z',
    customerName: 'Jennifer Lee',
    customerEmail: 'jlee@email.com',
    paymentMethod: 'Visa',
    cardLast4: '9876',
    merchantName: 'XYZ Shop',
    merchantId: 'MERCH-002',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Admin User',
      approverEmail: 'admin@merchant.com',
      status: 'approved',
      timestamp: '2024-01-17T08:05:00Z',
      notes: 'System detected billing error, auto-approved'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-17T08:00:00Z',
      status: 'requested',
      description: 'Refund requested by system',
      actor: 'System'
    }, {
      id: '2',
      timestamp: '2024-01-17T08:05:00Z',
      status: 'approved',
      description: 'Refund auto-approved',
      actor: 'Admin User'
    }, {
      id: '3',
      timestamp: '2024-01-17T08:10:00Z',
      status: 'processing',
      description: 'Processing refund to original payment method'
    }, {
      id: '4',
      timestamp: '2024-01-17T08:15:00Z',
      status: 'failed',
      description: 'Refund failed - card expired'
    }],
    isPartial: false
  }];
  // Calculate statistics
  const totalRefunds = refunds.length;
  const pendingRefunds = refunds.filter(r => ['requested', 'pending_approval', 'approved', 'processing'].includes(r.status)).length;
  const completedRefunds = refunds.filter(r => r.status === 'completed').length;
  const totalRefundAmount = refunds.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.refundAmount, 0);
  // Filter refunds
  const filteredRefunds = refunds.filter(refund => {
    const matchesSearch = refund.refundId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || refund.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || refund.status === statusFilter;
    const matchesReason = reasonFilter === 'all' || refund.reason === reasonFilter;
    return matchesSearch && matchesStatus && matchesReason;
  });
  const getStatusBadge = (status: RefundStatus) => {
    const styles = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-10245">
        {labels[status]}
      </span>;
  };
  const getReasonLabel = (reason: RefundReason) => {
    const labels = {
      customer_request: 'Customer Request',
      duplicate_charge: 'Duplicate Charge',
      fraudulent: 'Fraudulent',
      product_not_received: 'Product Not Received',
      product_defective: 'Product Defective',
      service_unsatisfactory: 'Service Unsatisfactory',
      billing_error: 'Billing Error',
      other: 'Other'
    };
    return labels[reason];
  };
  return <div className="space-y-6" data-id="element-10246">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10247">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-10248">
          <div className="flex items-center justify-between" data-id="element-10249">
            <div data-id="element-10250">
              <div className="text-sm font-medium text-gray-500" data-id="element-10251">
                Total Refunds
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-10252">
                {totalRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-10253">
              <RefreshCwIcon size={20} className="text-blue-600" data-id="element-10254" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-10255">
          <div className="flex items-center justify-between" data-id="element-10256">
            <div data-id="element-10257">
              <div className="text-sm font-medium text-gray-500" data-id="element-10258">
                Pending Refunds
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-10259">
                {pendingRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-10260">
              <ClockIcon size={20} className="text-yellow-600" data-id="element-10261" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-10262">
          <div className="flex items-center justify-between" data-id="element-10263">
            <div data-id="element-10264">
              <div className="text-sm font-medium text-gray-500" data-id="element-10265">
                Completed Refunds
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-10266">
                {completedRefunds}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-10267">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-10268" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-10269">
          <div className="flex items-center justify-between" data-id="element-10270">
            <div data-id="element-10271">
              <div className="text-sm font-medium text-gray-500" data-id="element-10272">
                Total Refunded
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-10273">
                THB {totalRefundAmount.toLocaleString('en-US')}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-10274">
              <DollarSignIcon size={20} className="text-red-600" data-id="element-10275" />
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-10276">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-10277">
          <div className="relative flex-grow" data-id="element-10278">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10279">
              <SearchIcon size={16} className="text-gray-400" data-id="element-10280" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by refund ID, transaction ID, or customer..." data-id="element-10281" />
          </div>
          <div className="flex gap-2" data-id="element-10282">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as RefundStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-10283">
              <option value="all" data-id="element-10284">All Statuses</option>
              <option value="requested" data-id="element-10285">Requested</option>
              <option value="pending_approval" data-id="element-10286">Pending Approval</option>
              <option value="approved" data-id="element-10287">Approved</option>
              <option value="processing" data-id="element-10288">Processing</option>
              <option value="completed" data-id="element-10289">Completed</option>
              <option value="rejected" data-id="element-10290">Rejected</option>
              <option value="failed" data-id="element-10291">Failed</option>
              <option value="cancelled" data-id="element-10292">Cancelled</option>
            </select>
            <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as RefundReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-10293">
              <option value="all" data-id="element-10294">All Reasons</option>
              <option value="customer_request" data-id="element-10295">Customer Request</option>
              <option value="duplicate_charge" data-id="element-10296">Duplicate Charge</option>
              <option value="fraudulent" data-id="element-10297">Fraudulent</option>
              <option value="product_not_received" data-id="element-10298">Product Not Received</option>
              <option value="product_defective" data-id="element-10299">Product Defective</option>
              <option value="service_unsatisfactory" data-id="element-10300">
                Service Unsatisfactory
              </option>
              <option value="billing_error" data-id="element-10301">Billing Error</option>
              <option value="other" data-id="element-10302">Other</option>
            </select>
          </div>
        </div>
        {/* Refunds List */}
        <div className="overflow-x-auto" data-id="element-10303">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-10304">
            <thead className="bg-gray-50" data-id="element-10305">
              <tr data-id="element-10306">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10307">
                  Refund
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10308">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10309">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10310">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10311">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10312">
                  Requested
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10313">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-10314">
              {filteredRefunds.map(refund => <tr key={refund.id} onClick={() => navigate(`/refund-detail/${refund.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-10315">
                  <td className="px-6 py-4" data-id="element-10316">
                    <div data-id="element-10317">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-10318">
                        {refund.refundId}
                        {refund.isPartial && <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded" data-id="element-10319">
                            Partial
                          </span>}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-10320">
                        {refund.transactionId}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10321">
                    <div data-id="element-10322">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10323">
                        {refund.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-10324">
                        {refund.customerEmail}
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-10325">
                        {refund.paymentMethod}{' '}
                        {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10326">
                    <div data-id="element-10327">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10328">
                        THB {refund.refundAmount.toLocaleString('en-US')}
                      </div>
                      {refund.isPartial && <div className="text-xs text-gray-500" data-id="element-10329">
                          of THB {refund.originalAmount.toLocaleString('en-US')}
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10330">
                    <div className="text-sm text-gray-900" data-id="element-10331">
                      {getReasonLabel(refund.reason)}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10332">{getStatusBadge(refund.status)}</td>
                  <td className="px-6 py-4" data-id="element-10333">
                    <div className="text-sm text-gray-900" data-id="element-10334">
                      {new Date(refund.requestedAt).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-10335">
                      by {refund.requestedBy}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-10336">
                    <button onClick={e => {
                  e.stopPropagation();
                  navigate(`/refund-detail/${refund.id}`);
                }} className="text-blue-600 hover:text-blue-900 font-medium" data-id="element-10337">
                      View Details
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredRefunds.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-10338">
            <RefreshCwIcon size={48} className="text-gray-400 mb-4" data-id="element-10339" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-10340">
              No refunds found
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-10341">
              No refunds match your current search criteria. Try adjusting your
              filters.
            </p>
          </div>}
      </div>
    </div>;
}